/**
 * Configuration Sidebar (S2-T2.3; advanced controls collapsed behind a
 * "More options" disclosure in S5-T2.5, so the rail isn't a config wall
 * beside the magic moment).
 *
 * Inspector-pattern controls for the StampingConfig: prefix, start number,
 * digit count, position, theme, opacity, endorsement. Controlled component,
 * it renders `config` and emits the next config via `onChange`. The owner
 * (App) holds the state and persists it through LocalStore. Live re-render of
 * the preview off these changes is S2-T2.4.
 */

import {
  STAMP_POSITIONS,
  STAMPING_THEMES,
  type StampPosition,
  type StampingConfig,
  type StampingTheme,
} from "../../engine/models";

import styles from "./ConfigRail.module.css";

const POSITION_LABELS: Record<StampPosition, string> = {
  topLeft: "Top Left",
  topCenter: "Top Center",
  topRight: "Top Right",
  bottomLeft: "Bottom Left",
  bottomCenter: "Bottom Center",
  bottomRight: "Bottom Right",
};

// Named by the stamp's background, not its text, so it reads plainly: "Black"
// is the black-background chip, "White outline" is the white chip with a stroke.
const THEME_LABELS: Record<StampingTheme, string> = {
  blackOnWhite: "White",
  whiteOnBlack: "Black",
  blackOnWhiteWithStroke: "White outline",
  whiteOnBlackWithStroke: "Black outline",
};

interface ConfigRailProps {
  config: StampingConfig;
  onChange: (config: StampingConfig) => void;
}

export function ConfigRail({ config, onChange }: ConfigRailProps) {
  const set = <K extends keyof StampingConfig>(key: K, value: StampingConfig[K]) =>
    onChange({ ...config, [key]: value });

  // Theme swatches show the user's own prefix in each style. Sample chip shows
  // the exact stamp text they'll get, mirroring the Mac inspector pattern.
  const swatchLabel = (config.prefix.slice(0, 4) || "Aa").toUpperCase();
  const sampleText = `${config.prefix}${String(config.startNumber).padStart(config.digitCount, "0")}`;
  const firstEndorsementLine = config.endorsement.split("\n").find((l) => l.trim()) ?? "";

  return (
    <aside className={styles.rail} aria-label="Configuration">
      <p className={styles.title}>Configuration</p>

      <label className={styles.field}>
        <span className={styles.label}>Prefix</span>
        <input
          className={styles.input}
          type="text"
          value={config.prefix}
          onChange={(e) => set("prefix", e.target.value)}
        />
      </label>

      <label className={styles.field}>
        <span className={styles.label}>Start number</span>
        <input
          className={styles.input}
          type="number"
          min={0}
          value={config.startNumber}
          onChange={(e) => set("startNumber", Number.parseInt(e.target.value, 10) || 0)}
        />
      </label>

      <div className={styles.field}>
        <span className={styles.label}>Position</span>
        <div className={styles.positions} role="group" aria-label="Position">
          {STAMP_POSITIONS.map((pos) => (
            <button
              key={pos}
              type="button"
              className={`${styles.posButton} ${config.position === pos ? styles.posActive : ""}`}
              aria-pressed={config.position === pos}
              onClick={() => set("position", pos)}
            >
              {POSITION_LABELS[pos]}
            </button>
          ))}
        </div>
      </div>

      <details className={styles.more}>
        <summary className={styles.moreSummary}>More options</summary>

        <label className={styles.field}>
          <span className={styles.label}>Digit Count</span>
          <input
            className={styles.input}
            type="number"
            min={1}
            max={12}
            value={config.digitCount}
            onChange={(e) => set("digitCount", Number.parseInt(e.target.value, 10) || 1)}
          />
        </label>

        <div className={styles.field}>
          <span className={styles.label}>Font</span>
          <select className={styles.input} disabled defaultValue="system" aria-label="Font">
            <option value="system">System default</option>
          </select>
          <span className={styles.hint}>Any font on your Mac. Only in the Mac app.</span>
        </div>

        <div className={styles.field}>
          <span className={styles.label}>Theme</span>
          <div className={styles.themeSwatches} role="group" aria-label="Theme">
            {STAMPING_THEMES.map((theme) => (
              <div key={theme} className={styles.themeOption}>
                <button
                  type="button"
                  className={`${styles.themeChip} ${styles[`theme_${theme}`]} ${
                    config.theme === theme ? styles.themeActive : ""
                  }`}
                  aria-pressed={config.theme === theme}
                  aria-label={THEME_LABELS[theme]}
                  onClick={() => set("theme", theme)}
                >
                  {swatchLabel}
                </button>
                <span className={styles.themeName}>{THEME_LABELS[theme]}</span>
              </div>
            ))}
          </div>
        </div>

      <div className={styles.field}>
        <span className={styles.label}>Opacity</span>
        <div className={styles.opacityRow}>
          <input
            type="range"
            min={0.05}
            max={1}
            step={0.05}
            value={config.opacity}
            aria-label="Opacity"
            onChange={(e) => set("opacity", Number.parseFloat(e.target.value))}
            style={{ flex: 1 }}
          />
          <span className={styles.opacityValue}>{config.opacity.toFixed(2)}</span>
        </div>
      </div>

      <label className={styles.field}>
        <span className={styles.label}>Endorsement</span>
        <textarea
          className={styles.textarea}
          value={config.endorsement}
          placeholder="Optional, one line per row"
          onChange={(e) => set("endorsement", e.target.value)}
        />
      </label>
      </details>

      <div className={styles.sample} aria-label="Sample">
        <span className={styles.sampleLabel}>Sample</span>
        <div className={`${styles.sampleChip} ${styles[`theme_${config.theme}`]}`}>
          <span className={styles.sampleText}>{sampleText}</span>
          {firstEndorsementLine && (
            <span className={styles.sampleEndorsement}>{firstEndorsementLine}</span>
          )}
        </div>
      </div>
    </aside>
  );
}
